import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useParams2 } from "../contexts/ParamsContext";
import ConfirmModal from "../components/ConfirmModal";

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const STATUTS = {
  disponible:    { label: "Disponible",     color: "#27ae60" },
  reserve:       { label: "Réservé",        color: "#f39c12" },
  emprunte:      { label: "Emprunté",       color: "#e74c3c" },
  indisponible:  { label: "Indisponible",   color: "#888"    },
};
// Etats des reservations personnelles (anciennement page "Mes reservations")
const RESA_STATUT = {
  en_attente: { label: "En attente de validation", color: "#f39c12",
                desc: "Votre demande est en attente que le libraire la valide." },
  validee:    { label: "Validée", color: "#1d4f91",
                desc: "Venez chercher le livre aux horaires d'ouverture." },
  retiree:    { label: "Empruntée", color: "#27ae60", desc: "Livre en votre possession." },
  rendue:     { label: "Rendue", color: "#888" },
  refusee:    { label: "Refusée", color: "#e74c3c" },
  annulee:    { label: "Annulée", color: "#888" },
};

const API_BASE = process.env.REACT_APP_API_BASE || "/api";

function fmtFR(iso) {
  if (!iso) return "";
  return new Date(iso + (iso.includes("T") ? "" : "T00:00:00"))
    .toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

function joursRestants(dateRetour) {
  if (!dateRetour) return null;
  const retour = new Date(dateRetour + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((retour - now) / (1000 * 60 * 60 * 24));
}

export default function Bibliotheque() {
  const params = useParams2();
  const { user } = useAuth();
  const [livres, setLivres] = useState([]);
  const [horaires, setHoraires] = useState([]);
  const [fermetures, setFermetures] = useState([]);
  const [vacances, setVacances] = useState(null);
  const [mesResas, setMesResas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/bibliotheque/livres"),
      api.get("/bibliotheque/horaires"),
    ]).then(([r1, r2]) => {
      setLivres(r1.items || []);
      setHoraires(r2.horaires || []);
      setFermetures(r2.fermetures || []);
      setVacances(r2.vacances || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const reloadResas = () => {
    api.get("/bibliotheque/reservations/me").then(setMesResas).catch(() => {});
  };
  useEffect(() => { if (user) reloadResas(); }, [user]);

  const livresFiltres = useMemo(() => {
    const q = search.trim().toLowerCase();
    return livres.filter((l) => {
      if (statutFilter && l.statut !== statutFilter) return false;
      if (!q) return true;
      return (
        l.titre.toLowerCase().includes(q) ||
        (l.auteur || "").toLowerCase().includes(q) ||
        (l.isbn || "").includes(q) ||
        (l.genre || "").toLowerCase().includes(q)
      );
    });
  }, [livres, search, statutFilter]);

  const compteurs = useMemo(() => {
    const c = { total: livres.length };
    Object.keys(STATUTS).forEach((s) => {
      c[s] = livres.filter((l) => l.statut === s).length;
    });
    return c;
  }, [livres]);

  const handleCancel = async () => {
    if (!confirmCancel) return;
    try {
      await api.del(`/bibliotheque/reservations/${confirmCancel.id}`);
      setConfirmCancel(null);
      reloadResas();
    } catch (e) {
      alert("Erreur : " + (e.message || e));
      setConfirmCancel(null);
    }
  };

  const fermeVacances = vacances && vacances.en_vacances;

  return (
    <div className="container">
      <h1>Bibliothèque municipale</h1>
      <p className="muted">
        {params.bibliotheque_description ||
          "Située derrière l'école du village, la bibliothèque municipale vous accueille pour découvrir et emprunter des livres."}
      </p>

      {/* Bandeau fermeture vacances scolaires (automatique, zone B / Le Mans) */}
      {fermeVacances && (
        <div className="biblio-vacances-banner">
          <strong>📕 Bibliothèque fermée pendant les vacances scolaires.</strong>
          {vacances.actuelle && (
            <span> {vacances.actuelle.description} — réouverture le {fmtFR(vacances.actuelle.fin)}.</span>
          )}
        </div>
      )}
      {!fermeVacances && vacances && vacances.prochaine && (
        <div className="biblio-vacances-info">
          ℹ️ Prochaine fermeture pour les vacances scolaires :
          {" "}<strong>{vacances.prochaine.description}</strong>,
          du {fmtFR(vacances.prochaine.debut)} au {fmtFR(vacances.prochaine.fin)}.
        </div>
      )}

      {/* Mes réservations (integre, remplace l'ancienne page dediee) */}
      {user && mesResas && (
        <MesReservationsBloc data={mesResas} onCancel={setConfirmCancel} />
      )}

      {/* En-tête horaires + infos pratiques */}
      <div className="biblio-header">
        <div className="card biblio-info">
          <h3 style={{ marginTop: 0 }}>📅 Horaires d'ouverture</h3>
          {horaires.length === 0 ? (
            <p className="muted">Horaires non disponibles</p>
          ) : (
            <table className="biblio-horaires-table">
              <tbody>
                {horaires.map((h) => (
                  <tr key={h.jour} className={h.ferme ? "biblio-ferme" : ""}>
                    <td>{JOURS[h.jour]}</td>
                    <td>
                      {h.ferme ? (
                        <span className="muted">Fermé</span>
                      ) : (
                        <strong>{h.heure_ouverture} – {h.heure_fermeture}</strong>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {fermetures.length > 0 && (
            <div style={{ marginTop: "1rem", paddingTop: "0.8rem",
                          borderTop: "1px dashed rgba(0,0,0,0.1)" }}>
              <h4 style={{ fontSize: "0.95em", marginTop: 0 }}>Fermetures exceptionnelles</h4>
              {fermetures.map((f) => (
                <div key={f.id} style={{ fontSize: "0.88em", marginBottom: 4 }}>
                  <strong>Du {fmtFR(f.date_debut)}</strong>
                  {f.date_fin !== f.date_debut && <> au <strong>{fmtFR(f.date_fin)}</strong></>}
                  {f.motif && <span className="muted"> – {f.motif}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card biblio-info">
          <h3 style={{ marginTop: 0 }}>📍 Informations pratiques</h3>
          {params.bibliotheque_adresse && (
            <p><strong>Adresse :</strong><br/>{params.bibliotheque_adresse}</p>
          )}
          {params.bibliotheque_telephone && (
            <p><strong>Téléphone :</strong> {params.bibliotheque_telephone}</p>
          )}
          {params.bibliotheque_email && (
            <p><strong>Email :</strong>{" "}
              <a href={`mailto:${params.bibliotheque_email}`}>
                {params.bibliotheque_email}
              </a></p>
          )}
          <div style={{ marginTop: "1rem", padding: "0.8rem",
                        background: "rgba(29, 79, 145, 0.06)",
                        borderRadius: 6, fontSize: "0.9em" }}>
            <strong>💡 Comment emprunter ?</strong><br/>
            {user ? (
              <>Cliquez sur un livre disponible et utilisez le bouton « Réserver ».
              Vous serez prévenu(e) lorsque votre réservation sera validée.</>
            ) : (
              <>Vous devez disposer d'un compte activé sur le site.
              {" "}<Link to="/inscription">Créer un compte</Link> ou{" "}
              <Link to="/connexion">se connecter</Link>.</>
            )}
          </div>
        </div>
      </div>

      {/* Statistiques rapides */}
      <div className="biblio-stats">
        <div className="biblio-stat-card">
          <div className="biblio-stat-num">{compteurs.total}</div>
          <div className="biblio-stat-label">livres au catalogue</div>
        </div>
        <div className="biblio-stat-card" style={{ borderLeftColor: STATUTS.disponible.color }}>
          <div className="biblio-stat-num" style={{ color: STATUTS.disponible.color }}>
            {compteurs.disponible}
          </div>
          <div className="biblio-stat-label">disponibles</div>
        </div>
        <div className="biblio-stat-card" style={{ borderLeftColor: STATUTS.emprunte.color }}>
          <div className="biblio-stat-num" style={{ color: STATUTS.emprunte.color }}>
            {compteurs.emprunte || 0}
          </div>
          <div className="biblio-stat-label">empruntés</div>
        </div>
        <div className="biblio-stat-card" style={{ borderLeftColor: STATUTS.reserve.color }}>
          <div className="biblio-stat-num" style={{ color: STATUTS.reserve.color }}>
            {compteurs.reserve || 0}
          </div>
          <div className="biblio-stat-label">réservés</div>
        </div>
      </div>

      {/* Recherche + filtres */}
      <div className="biblio-search">
        <input
          type="text"
          placeholder="Rechercher par titre, auteur, ISBN, genre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="biblio-search-input"
        />
        <select
          value={statutFilter}
          onChange={(e) => setStatutFilter(e.target.value)}
          className="biblio-filter-select"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(STATUTS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {loading && <p className="muted">Chargement du catalogue…</p>}

      {!loading && livresFiltres.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "2rem" }}>
          <p className="muted" style={{ margin: 0 }}>
            {livres.length === 0 ? "Le catalogue est vide pour le moment." :
                                   "Aucun livre ne correspond à votre recherche."}
          </p>
        </div>
      )}

      {!loading && livresFiltres.length > 0 && (
        <div className="biblio-grid">
          {livresFiltres.map((l) => {
            const statut = STATUTS[l.statut] || STATUTS.indisponible;
            return (
              <Link key={l.id} to={`/bibliotheque/livre/${l.id}`} className="biblio-card">
                <div className="biblio-card-cover">
                  {l.cover ? (
                    <img src={`${API_BASE}${l.cover}`} alt={l.titre} />
                  ) : (
                    <div className="biblio-card-no-cover">📕</div>
                  )}
                  <div className="biblio-card-statut" style={{ background: statut.color }}>
                    {statut.label}
                  </div>
                </div>
                <div className="biblio-card-body">
                  <div className="biblio-card-titre">{l.titre}</div>
                  {l.auteur && <div className="biblio-card-auteur">{l.auteur}</div>}
                  {l.genre && (
                    <div className="biblio-card-genre">
                      <span>{l.genre}</span>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {confirmCancel && (
        <ConfirmModal
          titre="Annuler la réservation"
          message={`Annuler définitivement la réservation de "${confirmCancel.livre_titre}" ?`}
          confirmLabel="Annuler la réservation"
          danger
          onConfirm={handleCancel}
          onCancel={() => setConfirmCancel(null)}
        />
      )}
    </div>
  );
}

// Bloc "Mes réservations" integre a la page Bibliotheque (remplace la page dediee).
function MesReservationsBloc({ data, onCancel }) {
  const actives = data.items.filter((r) => ["en_attente", "validee", "retiree"].includes(r.statut));
  const historique = data.items.filter((r) => ["rendue", "refusee", "annulee"].includes(r.statut));

  if (data.items.length === 0) return null;

  return (
    <div className="card biblio-mes-resas">
      <h2 style={{ marginTop: 0 }}>Mes réservations</h2>

      {data.a_des_retards && (
        <div className="error" style={{ marginBottom: "1rem" }}>
          ⚠ Vous avez un ou plusieurs livres en retard. Merci de les rendre rapidement.
        </div>
      )}

      {actives.length === 0 ? (
        <p className="muted">Vous n'avez aucune réservation active.</p>
      ) : (
        <div className="resa-grid">
          {actives.map((r) => {
            const info = RESA_STATUT[r.statut];
            const jours = joursRestants(r.date_retour_prevue);
            const enRetard = jours !== null && jours < 0;
            const bientotDu = jours !== null && jours >= 0 && jours <= 3;
            return (
              <div key={r.id} className="resa-card">
                <div className="resa-card-head">
                  <Link to={`/bibliotheque/livre/${r.livre_id}`} className="resa-titre">
                    {r.livre_titre}
                  </Link>
                  {r.livre_auteur && <div className="resa-auteur">{r.livre_auteur}</div>}
                </div>
                <div className="resa-statut" style={{ background: info.color }}>{info.label}</div>
                {info.desc && (
                  <p className="muted" style={{ fontSize: "0.88em", marginTop: "0.6rem" }}>{info.desc}</p>
                )}
                {r.statut === "retiree" && r.date_retour_prevue && (
                  <div className="resa-retour">
                    <strong>Retour prévu :</strong> {fmtFR(r.date_retour_prevue)}
                    {enRetard && <div className="resa-retard">⚠ En retard de {-jours} jour{-jours > 1 ? "s" : ""}</div>}
                    {bientotDu && !enRetard && (
                      <div className="resa-bientot">
                        ⏰ {jours === 0 ? "À rendre aujourd'hui" : `À rendre dans ${jours} jour${jours > 1 ? "s" : ""}`}
                      </div>
                    )}
                  </div>
                )}
                {r.commentaire_libraire && (
                  <div className="resa-comment">
                    <strong>Note du libraire :</strong> {r.commentaire_libraire}
                  </div>
                )}
                {r.statut === "en_attente" && (
                  <button className="link-btn danger" style={{ marginTop: "0.8rem" }}
                          onClick={() => onCancel(r)}>
                    Annuler ma demande
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {historique.length > 0 && (
        <details style={{ marginTop: "1rem" }}>
          <summary style={{ cursor: "pointer", fontWeight: 500 }}>
            Historique ({historique.length})
          </summary>
          <table className="biblio-admin-table" style={{ marginTop: "0.6rem" }}>
            <thead>
              <tr><th>Livre</th><th>Statut</th><th>Demande</th><th>Retour</th></tr>
            </thead>
            <tbody>
              {historique.map((r) => {
                const info = RESA_STATUT[r.statut];
                return (
                  <tr key={r.id}>
                    <td><Link to={`/bibliotheque/livre/${r.livre_id}`}>{r.livre_titre}</Link></td>
                    <td><span style={{ color: info.color, fontWeight: 500 }}>● {info.label}</span></td>
                    <td>{fmtFR(r.date_demande.split("T")[0])}</td>
                    <td>{r.date_retour_effective ? fmtFR(r.date_retour_effective) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
